import { useFlag } from '@reflag/react-sdk';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';

import { UserTheme } from '@gitbook/app-database';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
} from '@gitbook/spine-react';

import { bgGradient } from '../../../assets';
import { AnimatedTranslationIcon, RequestFeatureButton } from '../../../Components';
import { apiQuery, userThemeAtom } from '../../../state';
import { createDocumentationURL } from '../../../utils';
import emptyStateBgImg from './assets/empty-state-bg.svg';
import emptyStateBgDarkImg from './assets/empty-state-bg-dark.svg';
import { CreateTranslatedSpaceButton } from './CreateTranslatedSpaceButton';

/**
 * Blankslate for the translations screen.
 */
export function TranslationsBlankslate(props: { organizationId: string }) {
    const userTheme = useRecoilValue(userThemeAtom);
    const { organizationId } = props;

    const translationFeatures = useFlag('TRANSLATIONS');

    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', { organizationId })
    );

    return (
        <div className="flex flex-1 flex-row items-center justify-center">
            <div
                className="absolute inset-0"
                style={{
                    background: `center / cover no-repeat url(${bgGradient.light})`,
                }}
            />
            <div
                className="absolute inset-0 hidden md:block"
                style={{
                    background: `center no-repeat url(${userTheme === UserTheme.Dark ? emptyStateBgDarkImg : emptyStateBgImg})`,
                }}
            />
            <div>
                <EmptyState className="relative bg-base shadow-elevated">
                    <EmptyStateIcon>
                        <AnimatedTranslationIcon size={32} loop={false} />
                    </EmptyStateIcon>
                    <Heading>Automatically translate your content</Heading>
                    {organization.permissions.createContent ? (
                        <>
                            <Description>
                                Use AI to automatically translate your content into multiple
                                languages.
                            </Description>
                            {translationFeatures.isEnabled ? (
                                <EmptyStateActions>
                                    <Button
                                        label="Read the docs"
                                        kind="secondary"
                                        href={createDocumentationURL(
                                            'creating-content/translations'
                                        )}
                                        target="_blank"
                                    />
                                    <CreateTranslatedSpaceButton />
                                </EmptyStateActions>
                            ) : (
                                <EmptyStateActions>
                                    <RequestFeatureButton flagKey="TRANSLATIONS" />
                                </EmptyStateActions>
                            )}
                        </>
                    ) : (
                        <Description>
                            You don't seem to have permission to create translations. Reach out to
                            your organization's admin to get started.
                        </Description>
                    )}
                </EmptyState>
            </div>
        </div>
    );
}
